import TrackPointsTableWrapper from "./TrackPointsTableWrapper";
import HistoryInfiniteScrollTable from "./HistoryInfiniteScrollTable";
import "./trackpoint.scss";
import {
  useGetVehicleRoutingQuery,
  useGetFormattedVehicleRouteQuery,
  useLazyGetVehicleRoutingQuery,
  useLazyGetFormattedVehicleRouteQuery,
} from "../../services";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { QIErrorBoundary, QISpinner } from "../../../../components";
import moment from "moment";
import {
  extractKeyValuePairsTrackPoints,
  flattenObject,
  removeDuplicates,
  uniqueArray,
  spreadDeviceDataObject,
  transformStandstillData,
} from "../../utils";
import { NoData } from "../../../Shared";
import { SelectedAssetSummary } from "../../Shared";
import { useSelector, useDispatch } from "react-redux";
import { setLiveTrackData } from "../../../../reduxStore/features";
import {
  buildChronologicalTrackPoints,
  fillMissingSpeeds,
  mergeHistoryRows,
} from "./helper";
import { buildLiveTableRows } from "./liveTable";

const NOOP = () => {};

const EMPTY_ROWS = [];
const LIVE_PAGE_SIZE = 20;

const LiveTrackPoints = ({
  deviceId,
  selectedVehiclePlate,
  liveData,
  selectedRange,
  setDeviceId = NOOP,
  setMapPoint = NOOP,
  clearSelectionKey,
  type,
  currentTab,
  setSelectedTrackPointData,
  deviceData,
  setTrackpointsHistoryData,
  setIsTrackPointsHistoryDataCollected = NOOP,
  skipQuery = false,
  // Canonical telemetry plumbing from VehicleContainer / AlternateIndex (spec
  // tasks 3.7 / 3.9). `onRoutePackets(plateNumber, packets)` hydrates the
  // canonical store with the loaded route page, and `telemetry` exposes the
  // canonical getters the live table and summary read.
  //
  // When `telemetry` is absent (the Devices containers reuse this component) the
  // previous independent-merge behavior is kept unchanged.
  onRoutePackets = NOOP,
  telemetry = null,
  // Selected asset row + realtime map data used to render the status / plate /
  // speed summary above the table.
  selectedAsset = null,
  mapData = null,
  summaryCardType = "Vehicles",
}) => {
  const variableData = useSelector((state) => state.common.variableDatas);
  const [selectedId, setSelectedId] = useState();
  const dispatch = useDispatch();
  let liveTrackData = useSelector((state) => state.common.liveTrackData);

  const [selectedColumns, setSelectedColumns] = useState([]);
  const [selectedColumnItems, setSelectedColumnItems] = useState([
    "source_time",
    "gps.position.address.name",
    "ignition",
    "velocity.speed",
    /* "mileage", */
    "voltage.external",
    "voltage.internal",
  ]);
  const [columnData, setColumnData] = useState([]);
  const [localLiveData, setLocalLiveData] = useState([]);
  const [highlight, setHighlight] = useState(null);
  useEffect(() => {
    setHighlight(null);
  }, [clearSelectionKey]);
  const [routePages, setRoutePages] = useState({ context: "", rows: [], loadedCount: 0, nextPage: 1, exhausted: false });
  const loadingContextRef = useRef(null);
  const [loadingContext, setLoadingContext] = useState(null);
  const [loadMoreDevices] = useLazyGetVehicleRoutingQuery();
  const [loadMoreVehicles] = useLazyGetFormattedVehicleRouteQuery();

  /**
   * Task 3.9: the live table and the live summary are read from the canonical
   * telemetry store instead of being merged here. The store already reconciled
   * the route response and the SignalR stream by speed observation time, so this
   * table and the sidebar cannot show different speeds for the same vehicle
   * (#119201).
   */
  const isCanonical = Boolean(telemetry?.getTableDataByPlateNumber) && Boolean(selectedVehiclePlate);

  const canonicalRows = isCanonical
    ? (telemetry.getTableDataByPlateNumber(selectedVehiclePlate) ?? EMPTY_ROWS)
    : EMPTY_ROWS;
  const canonicalMovement = isCanonical
    ? (telemetry.getCurrentMovementByPlateNumber?.(selectedVehiclePlate) ?? null)
    : null;

  /**
   * Real packets only, newest first. No aggregate row is synthesized and no speed
   * is filled in from another packet (design 3.5). `buildLiveTableRows` marks the
   * row backing the canonical movement (`is_current_movement`) by matching the
   * newest **speed-bearing** row against `movement.observedAt` - never the first
   * row, which may legitimately carry no speed (bugfix.md 2.8).
   */
  const canonicalTableRows = useMemo(
    () =>
      isCanonical
        ? buildLiveTableRows(canonicalRows, { movement: canonicalMovement })
        : EMPTY_ROWS,
    [isCanonical, canonicalRows, canonicalMovement]
  );

  // Stabilize query timestamps: compute once from selectedRange and only recompute
  // when selectedRange actually changes reference (i.e. when vehicle selection changes).
  // This prevents re-renders from producing slightly different moment().valueOf() numbers.
  const stableStartTime = useMemo(
    () => moment(selectedRange.startDate).valueOf(),
    [selectedRange]
  );
  const stableEndTime = useMemo(
    () => moment(selectedRange.endDate).valueOf(),
    [selectedRange]
  );
  const routeContext = `${type}:${type === "Devices" ? deviceId || "" : ""}:${selectedVehiclePlate || ""}:${stableStartTime}:${stableEndTime}`;
  const routeContextRef = useRef(routeContext);
  routeContextRef.current = routeContext;
  const visibleRoutePages = routePages.context === routeContext ? routePages : null;
  const liveRows = isCanonical ? canonicalTableRows : removeDuplicates(liveTrackData);
  const tableRows = (visibleRoutePages
    ? mergeHistoryRows(liveRows, visibleRoutePages.rows)
    : liveRows).filter((row) => row?.packet_type === "device_data");

  // Prevent duplicate API calls when switching vehicles.
  // When the user switches from vehicle A to vehicle B, the parent component updates
  // selectedVehiclePlate immediately but selectedRange (selectedRangeLive) is updated
  // in a useEffect that runs AFTER the render. This means on the first render after
  // switching, selectedVehiclePlate is "B" but selectedRange still has timestamps for "A".
  //
  // We track which vehicle the current selectedRange was last computed for using a ref.
  // If selectedVehiclePlate doesn't match, we skip the query until the range updates.
  const rangeComputedForRef = useRef(selectedVehiclePlate);
  const selectedRangeRef = useRef(selectedRange);

  // Update the ref when selectedRange changes (this means the parent effect has run)
  if (selectedRangeRef.current !== selectedRange) {
    selectedRangeRef.current = selectedRange;
    rangeComputedForRef.current = selectedVehiclePlate;
  }

  // If the current plate doesn't match the plate the range was computed for, skip query
  const isRangeStale = rangeComputedForRef.current !== selectedVehiclePlate;
  const shouldSkip = skipQuery || isRangeStale;

  //Fetch Track Points Data for Devices
  const {
    currentData: devicesResponse,
    isFetching: devicesIsFetching,
    isSuccess: devicesIsSuccess,
    error: devicesError,
  } = useGetVehicleRoutingQuery(
    {
      deviceId,
      start_time: stableStartTime,
      end_time: stableEndTime,
      per_page: LIVE_PAGE_SIZE,
      page: 0,
      isTime: true,
      noFields: true,
      plate_number: selectedVehiclePlate,
      type: type,
      source_id: [deviceId],
      // Load the newest page first so a range with more than `per_page` packets
      // cannot hide the newest packet on an ascending page zero (design "Route
      // Loading Strategy" / task 3.10, bugfix.md 1.7 / 2.7). Both the canonical
      // store and the map polyline re-sort internally, so DESC response order
      // keeps the live table newest-first and the polyline oldest-first (3.6).
      sortOrder: "DESC",
    },
    { skip: shouldSkip || type === "Vehicles" }
  );

  //Fetch Track Points Data for Vehicles
  const {
    currentData: vehiclesResponse,
    isFetching: vehiclesIsFetching,
    isSuccess: vehiclesIsSuccess,
    error: vehiclesError,
  } = useGetFormattedVehicleRouteQuery(
    {
      plate_number: selectedVehiclePlate,
      start_time: stableStartTime,
      end_time: stableEndTime,
      per_page: LIVE_PAGE_SIZE,
      page: 0,
      isTime: true,
      // Newest page first for the live summary (design "Route Loading Strategy" /
      // task 3.10). The store reconciles by speed observation time, so the newest
      // route packet is always loadable regardless of `total_count`.
      sortOrder: "DESC",
    },
    { skip: shouldSkip || type === "Devices" }
  );

  const response = type === "Devices" ? devicesResponse : vehiclesResponse;
  const isFetching = type === "Devices" ? devicesIsFetching : vehiclesIsFetching;
  const isSuccess = type === "Devices" ? devicesIsSuccess : vehiclesIsSuccess;
  const error = type === "Devices" ? devicesError : vehiclesError;
  const waitingForRows = Boolean(selectedVehiclePlate) && !error &&
    (!response || (response.data?.length > 0 && !visibleRoutePages && tableRows.length === 0));

  useEffect(() => {
    if (!response || !isSuccess || isFetching || shouldSkip) return;
    setRoutePages((previous) =>
      previous.context === routeContext
        ? previous
        : {
            context: routeContext,
            rows: (response.data || []).map((row) => flattenObject(row)),
            loadedCount: response.data?.length || 0,
            nextPage: 1,
            exhausted: false,
          }
    );
  }, [response, isSuccess, isFetching, shouldSkip, routeContext]);

  const hasMoreLiveRows =
    visibleRoutePages &&
    !visibleRoutePages.exhausted &&
    visibleRoutePages.loadedCount < (response?.total_count || 0);
  const handleLoadMore = useCallback(async () => {
    if (loadingContextRef.current === routeContext || isFetching || !hasMoreLiveRows) return;

    const page = visibleRoutePages.nextPage;
    const query = {
      start_time: stableStartTime,
      end_time: stableEndTime,
      per_page: LIVE_PAGE_SIZE,
      page,
      isTime: true,
      sortOrder: "DESC",
      ...(type === "Devices"
        ? { deviceId, noFields: true, plate_number: selectedVehiclePlate, type, source_id: [deviceId] }
        : { plate_number: selectedVehiclePlate }),
    };
    loadingContextRef.current = routeContext;
    setLoadingContext(routeContext);
    try {
      const result = await (type === "Devices" ? loadMoreDevices(query) : loadMoreVehicles(query)).unwrap();
      if (routeContextRef.current !== routeContext) return;
      const rows = result?.data || [];
      setRoutePages((previous) =>
        previous.context !== routeContext || previous.nextPage !== page
          ? previous
          : {
              ...previous,
              rows: mergeHistoryRows(previous.rows, rows.map((row) => flattenObject(row))),
              loadedCount: previous.loadedCount + rows.length,
              nextPage: page + 1,
              exhausted: rows.length === 0,
            }
      );
    } catch (error) {
      console.error("Unable to load more live track points:", error);
    } finally {
      if (loadingContextRef.current === routeContext) loadingContextRef.current = null;
      setLoadingContext((current) => current === routeContext ? null : current);
    }
  }, [routeContext, isFetching, hasMoreLiveRows, visibleRoutePages, stableStartTime, stableEndTime, type, deviceId, selectedVehiclePlate, loadMoreDevices, loadMoreVehicles]);

  useEffect(() => {
    if (isSuccess && !isFetching) {
      setIsTrackPointsHistoryDataCollected(true);
    }
  }, [isSuccess, isFetching]);

  useEffect(() => {
    if (response?.data?.length > 0) {
      const newDeviceId = response.data[0].source_id;
      // Only update deviceId when the value actually changes to prevent
      // unnecessary re-renders and cascading API calls in parent components
      if (newDeviceId && newDeviceId !== deviceId) {
        setDeviceId(newDeviceId);
      }

      // Hydrate the canonical telemetry store with the whole loaded route page
      // (design step 7/10). Reconciliation happens there by speed observation
      // time, so the sidebar and this summary can no longer disagree (#119201).
      // The old `onLatestMovementData` path - which picked a "latest" snapshot
      // here and re-labelled it `device_data` - is gone: selecting the canonical
      // observation is the store's job, not this component's.
      onRoutePackets(selectedVehiclePlate, response.data);
    } else {
      setMapPoint(null);
    }
  }, [response?.data, deviceId, onRoutePackets, selectedVehiclePlate, setDeviceId, setMapPoint]);

  // When deviceId or selectedVehiclePlate changes, reset highlight and mapPoint
  useEffect(() => {
    setHighlight(null);
    setMapPoint({});
  }, [deviceId, selectedVehiclePlate]);

  //Handle Row Selection
  const handleRowClick = (data) => {
    setDeviceId(data?.source_id);
    let gpsObj = {};
    if (data?.packet_id !== highlight) {
      gpsObj = {
        alt: data["gps.position.alt"],
        is_valid: data["gps.position.is_valid"],
        lat: data["gps.position.lat"],
        lng: data["gps.position.lng"],
        source_time: data["source_time"],
        direction: data["velocity.direction"],
      };
      setHighlight(data?.packet_id);
    } else {
      setHighlight(null);
    }

    setMapPoint(gpsObj);
    setSelectedTrackPointData(data);
    //setLiveDetailsBlockView("Details");
  };

  /**
   * Canonical mode keeps redux `liveTrackData` a *projection* of the canonical
   * store (the map still reads it) instead of a second, independently merged copy
   * of the same packets. Everything below that used to build that copy - route
   * flattening, realtime folding, forward-filled speeds - is skipped.
   */
  useEffect(() => {
    if (!isCanonical) return;

    dispatch(setLiveTrackData(canonicalTableRows));
  }, [isCanonical, canonicalTableRows, dispatch]);

  useEffect(() => {
    if (isCanonical) return;

    if (localLiveData?.length > 0) {
      // Sort descending (newest first) so the table always shows newest at top.
      // The API returns data in ascending order for the map polyline, but the
      // table must display it newest-first.
      const combined = [...liveTrackData, ...localLiveData].sort(
        (a, b) => new Date(b.source_time) - new Date(a.source_time)
      );

      dispatch(setLiveTrackData(combined));
      setLocalLiveData([]);
    }
  }, [localLiveData, isCanonical]);

  useEffect(() => {
    //Iteratively Collect Data and Stacked Together
    if (response) {
      if (!isCanonical) {
        fillMissingSpeeds(response?.data)?.forEach((item) => {
          if (item?.source_id === deviceId && item?.plate_number === selectedVehiclePlate) {
            let tempData = flattenObject(item);
            setLocalLiveData((prev) => [...prev, tempData]);
          }
        });
      }
      if (
        !isCanonical &&
        !response?.data?.length &&
        isSuccess &&
        !isFetching &&
        deviceData?.source_id === deviceId &&
        deviceData?.plate_number?.v === selectedVehiclePlate
      ) {
        if (liveTrackData?.length === 0) {
          let tempData = flattenObject(spreadDeviceDataObject(deviceData));
          tempData["data_from_api"] = true;
          dispatch(setLiveTrackData([tempData]));
        }
      }
    }
  }, [response, selectedRange.startDate, selectedVehiclePlate, deviceId, deviceData, isCanonical]);

  useEffect(() => {
    if (!visibleRoutePages) return;
    setTrackpointsHistoryData(
      buildChronologicalTrackPoints(visibleRoutePages.rows, deviceId, selectedVehiclePlate)
    );
  }, [visibleRoutePages?.rows, deviceId, selectedVehiclePlate, setTrackpointsHistoryData]);

  useEffect(() => {
    // Canonical mode: SignalR packets are already accumulated per plate by
    // `useWebSocket` and merged into the canonical store by observation time, so
    // folding them in again here (and forward-filling a missing speed from an
    // older row) would recreate the divergence this fix removes.
    if (isCanonical) return;

    // If time gap greater than 24 hr stop live data flow
    const currentTime = moment().format("YYYY-MM-DD HH:mm:ss");
    const selectedTime = moment(selectedRange.startDate).format("YYYY-MM-DD HH:mm:ss");
    const duration = moment.duration(moment(currentTime).diff(moment(selectedTime)));
    const hours = duration.asHours();

    //Real Time data flow
    /*
     - Real Time data will only flow if packet types equals "device_data"
     - Data will be coming if the time duration is last 24 hrs
     */
    let liveInfo = type === "Vehicles" ? liveData[selectedVehiclePlate] : liveData[deviceId];
    if (hours <= 25 && liveInfo && liveInfo?.packet_type === "device_data") {
      const flattenedLiveInfo = flattenObject(liveInfo);
      if (
        (flattenedLiveInfo["velocity.speed"] === null ||
          flattenedLiveInfo["velocity.speed"] === undefined ||
          flattenedLiveInfo["velocity.speed"] === "")
      ) {
        flattenedLiveInfo["velocity.speed"] = liveTrackData.find(
          (item) =>
            new Date(item.source_time).getTime() <=
              new Date(flattenedLiveInfo.source_time).getTime() &&
            item["velocity.speed"] !== null &&
            item["velocity.speed"] !== undefined &&
            item["velocity.speed"] !== ""
        )?.["velocity.speed"];
      }
      const tempData = removeDuplicates([flattenedLiveInfo, ...liveTrackData]);
      const filteredData = tempData?.filter((item) => item.data_from_api !== true);

      const sortedData =
        filteredData?.length > 0
          ? filteredData
              .sort((a, b) => new Date(a.source_time) - new Date(b.source_time))
              .reverse()
              .filter((item) => {
                return item?.source_id === deviceId || item?.plate_number === selectedVehiclePlate;
              })
          : [];

      dispatch(setLiveTrackData(sortedData));
    }
  }, [liveData, selectedRange.startDate, selectedVehiclePlate, deviceId, isCanonical]);

  useEffect(() => {
    // Get variable IDs from localStorage
    const storedVariableIds = localStorage.getItem("variable-ids");

    if (storedVariableIds && variableData && Object.keys(variableData).length > 0) {
      try {
        const variableIds = JSON.parse(storedVariableIds);
        const mappedColumns = [];

        variableIds.forEach((item, index) => {
          const variableId = item.id;

          // Find the corresponding variable in variableData by ID
          const matchingVariable = Object.values(variableData).find(
            (variable) => variable.id === variableId
          );

          if (matchingVariable) {
            // Create the key in the format: section_value.name (e.g., "voltage.internal")
            const key = matchingVariable.section_value
              ? `${matchingVariable.section_value}.${matchingVariable.name}`
              : matchingVariable.name;

            mappedColumns.push({
              name: key,
              id: index,
              enabled: true,
              key: key,
            });
          }
        });

        setColumnData(mappedColumns);
      } catch (error) {
        console.error("Error parsing variable IDs from localStorage:", error);
        // Fallback to empty array if parsing fails
        setColumnData([]);
      }
    } else {
      console.log("No variable IDs in localStorage or variableData not available yet");
      setColumnData([]);
    }
  }, [variableData]); // Only depend on variableData, not liveTrackData

  //Set Default columns
  useEffect(() => {
    setSelectedColumns([]);
    uniqueArray(columnData).map((item) => {
      if ([...new Set(selectedColumnItems)].includes(item?.key)) {
        setSelectedColumns((prev) => [...prev, item?.id]);
      }
    });
  }, [columnData, selectedColumnItems, selectedRange, deviceId, selectedVehiclePlate]);

  const message = () => {
    return {
      header: error?.liveTrackData?.message,
    };
  };

  return (
    <>
      {(selectedAsset || selectedVehiclePlate) && (
        <SelectedAssetSummary
          data={selectedAsset}
          plateNumber={selectedVehiclePlate}
          cardType={summaryCardType}
          mapData={mapData}
          telemetry={telemetry}
        />
      )}
      {error && <QIErrorBoundary message={message()} />}
      {(isFetching || waitingForRows) && (
        <div className="no-data">
          <QISpinner size="50px" />
        </div>
      )}
      {!isFetching && !waitingForRows && isSuccess && tableRows.length > 0 ? (
        <TrackPointsTableWrapper
          data={tableRows}
          selectedColumns={selectedColumns}
          setSelectedColumns={setSelectedColumns}
          setSelectedColumnItems={setSelectedColumnItems}
          isSuccess={isSuccess}
          variableData={variableData}
          tableHandlers={{
            rowHandler: handleRowClick,
          }}
          currentTab={currentTab}
          highlight={highlight}
          columnData={columnData}
          onScrollFrame={({ top }) => {
            if (top >= 0.9) handleLoadMore();
          }}
          isLoadingMore={loadingContext === routeContext}
          isEndOfData={Boolean(visibleRoutePages && !hasMoreLiveRows && loadingContext !== routeContext)}
        />
      ) : (
        isSuccess && response && !error && !isFetching && !waitingForRows && <NoData />
      )}
    </>
  );
};

export const HistoryTrackPoints = ({
  deviceId,
  selectedVehiclePlate,
  liveData,
  selectedRange,
  setResetCount,
  setDeviceId = () => {},
  setMapPoint = () => {},
  setDockAlign = () => {},
  type,
  currentTab,
  setSelectedTrackPointData,
  deviceData,
  setTrackpointsHistoryData,
  setStandstillData = () => {},
  telemetry = null,
  // Selected asset row + realtime map data used to render the status / plate /
  // speed summary above the table.
  selectedAsset = null,
  mapData = null,
  summaryCardType = "Vehicles",
}) => {
  const variableData = useSelector((state) => state.common.variableDatas);
  const [selectedId, setSelectedId] = useState();

  const [selectedColumns, setSelectedColumns] = useState([]);
  const [selectedColumnItems, setSelectedColumnItems] = useState([
    "source_time",
    "gps.position.address.name",
    "ignition",
    "velocity.speed",
    /* "mileage", */
    "voltage.external",
    "voltage.internal",
  ]);
  const perPage = 20;
  const [historyData, setHistoryData] = useState([]);
  const [historyDataContext, setHistoryDataContext] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [columnData, setColumnData] = useState([]);
  const [highlight, setHighlight] = useState(null);
  const processedPagesRef = useRef(new Set());
  const loadMoreLockedRef = useRef(false);

  const rangeStart = useMemo(
    () => moment(selectedRange.startDate).valueOf(),
    [selectedRange.startDate]
  );
  const rangeEnd = useMemo(
    () => moment(selectedRange.endDate).valueOf(),
    [selectedRange.endDate]
  );
  const historyContext = `${type}:${
    type === "Devices" ? deviceId || "" : selectedVehiclePlate || ""
  }:${rangeStart}:${rangeEnd}:${perPage}`;
  const visibleHistoryData = historyDataContext === historyContext ? historyData : EMPTY_ROWS;

  const isHistoryInitialMountRef = useRef(true);

  useEffect(() => {
    return () => {
      setResetCount((prev) => prev + 1);
      setPage(1);
    };
  }, [selectedVehiclePlate]);

  useEffect(() => {
    // Skip on initial mount to prevent date picker recalculating range on tab switch
    if (isHistoryInitialMountRef.current) {
      isHistoryInitialMountRef.current = false;
      return;
    }
    if (selectedVehiclePlate) {
      setResetCount((c) => c + 1);
    }
  }, [selectedVehiclePlate]);

  //Fetch Track Points Data for Devices
  const {
    currentData: devicesResponse,
    isFetching: devicesIsFetching,
    isSuccess: devicesIsSuccess,
    error: devicesError,
    refetch: refetchDevices,
  } = useGetVehicleRoutingQuery(
    {
      deviceId,
      start_time: rangeStart,
      end_time: rangeEnd,
      per_page: perPage,
      page: page - 1,
      isTime: true,
      noFields: true,
      plate_number: selectedVehiclePlate,
      type: type,
      source_id: [deviceId],
      // The History table must be globally newest-first before server pagination.
      sortOrder: "DESC",
    },
    { skip: type === "Vehicles" }
  );

  //Fetch Track Points Data for Vehicles
  const {
    currentData: vehiclesResponse,
    isFetching: vehiclesIsFetching,
    isSuccess: vehiclesIsSuccess,
    error: vehiclesError,
    refetch: refetchVehicles,
  } = useGetFormattedVehicleRouteQuery(
    {
      plate_number: selectedVehiclePlate,
      start_time: rangeStart,
      end_time: rangeEnd,
      per_page: perPage,
      page: page - 1,
      isTime: true,
      // Keep table intent separate from the chronological ASC map request.
      sortOrder: "DESC",
    },
    { skip: type === "Devices" }
  );

  const response = type === "Devices" ? devicesResponse : vehiclesResponse;
  const isFetching = type === "Devices" ? devicesIsFetching : vehiclesIsFetching;
  const isSuccess = type === "Devices" ? devicesIsSuccess : vehiclesIsSuccess;
  const error = type === "Devices" ? devicesError : vehiclesError;
  const refetch = type === "Devices" ? refetchDevices : refetchVehicles;

  // Extract and pass standstill data to parent component
  useEffect(() => {
    if (response && response.stand_still) {
      const transformedStandstills = transformStandstillData(response.stand_still);
      setStandstillData(transformedStandstills);
    } else {
      setStandstillData([]);
    }
  }, [response, setStandstillData]);

  useEffect(() => {
    if (response?.data?.length > 0) {
      const newDeviceId = response.data[0].source_id;
      if (newDeviceId && newDeviceId !== deviceId) {
        setDeviceId(newDeviceId);
      }
    } else {
      setMapPoint(null);
    }
  }, [response?.data]);

  useEffect(() => {
    setHighlight(null);
    setMapPoint({});
  }, [deviceId, selectedVehiclePlate]);

  //Handle Row Selection
  const handleRowClick = (data) => {
    setDeviceId(data?.source_id);
    let gpsObj = {};
    if (data?.packet_id !== highlight) {
      gpsObj = {
        alt: data["gps.position.alt"],
        is_valid: data["gps.position.is_valid"],
        lat: data["gps.position.lat"],
        lng: data["gps.position.lng"],
        source_time: data["source_time"],
        direction: data["velocity.direction"],
      };
      setHighlight(data?.packet_id);
    } else {
      setHighlight(null);
    }

    setMapPoint(gpsObj);
    setSelectedTrackPointData(data);
    //setLiveDetailsBlockView("Details");
  };

  useEffect(() => {
    setPage(1);
    setHistoryData([]);
    setHistoryDataContext(historyContext);
    setTotalCount(0);
    setTrackpointsHistoryData([]);
    setHighlight(null);
    processedPagesRef.current.clear();
    loadMoreLockedRef.current = false;
  }, [historyContext, setTrackpointsHistoryData]);

  useEffect(() => {
    if (!response || !isSuccess || isFetching) return;

    loadMoreLockedRef.current = false;
    setTotalCount(response.total_count || 0);

    const processedPageKey = `${historyContext}:${page}`;
    if (processedPagesRef.current.has(processedPageKey)) return;
    processedPagesRef.current.add(processedPageKey);

    if (response.data?.length > 0) {
      const flattenedPage = response.data.map((item) => flattenObject(item));
      setHistoryDataContext(historyContext);
      setHistoryData((previousRows) => mergeHistoryRows(previousRows, flattenedPage));
      return;
    }

    if (
      page === 1 &&
      deviceData?.source_id === deviceId &&
      deviceData?.plate_number?.v === selectedVehiclePlate
    ) {
      setHistoryDataContext(historyContext);
      setHistoryData([flattenObject(spreadDeviceDataObject(deviceData))]);
    }
  }, [
    response,
    isSuccess,
    isFetching,
    historyContext,
    page,
    deviceData,
    deviceId,
    selectedVehiclePlate,
  ]);

  useEffect(() => {
    setTrackpointsHistoryData(visibleHistoryData);
  }, [visibleHistoryData, setTrackpointsHistoryData]);

  useEffect(() => {
    if (error) {
      loadMoreLockedRef.current = false;
    }
  }, [error]);

  const hasMore = visibleHistoryData.length < totalCount;
  const handleLoadMore = useCallback(() => {
    if (loadMoreLockedRef.current || isFetching || !hasMore) return;

    loadMoreLockedRef.current = true;
    setPage((currentPage) => currentPage + 1);
  }, [hasMore, isFetching]);

  useEffect(() => {
    // Get variable IDs from localStorage
    const storedVariableIds = localStorage.getItem("variable-ids");

    if (storedVariableIds && variableData && Object.keys(variableData).length > 0) {
      try {
        const variableIds = JSON.parse(storedVariableIds);

        const mappedColumns = [];

        variableIds.forEach((item, index) => {
          const variableId = item.id;

          // Find the corresponding variable in variableData by ID
          const matchingVariable = Object.values(variableData).find(
            (variable) => variable.id === variableId
          );

          if (matchingVariable) {
            // Create the key in the format: section_value.name (e.g., "voltage.internal")
            const key = matchingVariable.section_value
              ? `${matchingVariable.section_value}.${matchingVariable.name}`
              : matchingVariable.name;

            mappedColumns.push({
              name: key,
              id: index,
              enabled: true,
              key: key,
            });
          }
        });

        setColumnData(mappedColumns);
      } catch (error) {
        console.error("Error parsing variable IDs from localStorage (History):", error);
        // Fallback to empty array if parsing fails
        setColumnData([]);
      }
    } else {
      console.log("No variable IDs in localStorage or variableData not available yet (History)");
      setColumnData([]);
    }
  }, [variableData]); // Only depend on variableData, not historyData

  //Set Default columns
  useEffect(() => {
    setSelectedColumns([]);
    uniqueArray(columnData).map((item) => {
      if ([...new Set(selectedColumnItems)].includes(item?.key)) {
        setSelectedColumns((prev) => [...prev, item?.id]);
      }
    });
  }, [columnData, selectedColumnItems, selectedRange, deviceId, selectedVehiclePlate]);

  const message = () => {
    return {
      header: error?.historyData?.message,
    };
  };

  return (
    <>
      {(selectedAsset || selectedVehiclePlate) && (
        <SelectedAssetSummary
          data={selectedAsset}
          plateNumber={selectedVehiclePlate}
          cardType={summaryCardType}
          mapData={mapData}
          telemetry={telemetry}
          variant="plate"
        />
      )}
      {error && <QIErrorBoundary message={message()} />}
      {isFetching && visibleHistoryData.length === 0 && (
        <div className="no-data">
          <QISpinner size="50px" />
        </div>
      )}
      {visibleHistoryData.length > 0 ? (
        <HistoryInfiniteScrollTable
          data={visibleHistoryData}
          selectedColumns={selectedColumns}
          setSelectedColumns={setSelectedColumns}
          setSelectedColumnItems={setSelectedColumnItems}
          hasMore={hasMore}
          isLoadingMore={isFetching}
          onLoadMore={handleLoadMore}
          variableData={variableData}
          tableHandlers={{ rowHandler: handleRowClick }}
          highlight={highlight}
          columnData={columnData}
        />
      ) : (
        isSuccess && !error && !isFetching && <NoData />
      )}
    </>
  );
};

export default LiveTrackPoints;
